// Storage — Cold Archive (Glacier Instant)
//
// Sibling of Raw Footage, NOT part of it. Files here are inert: no transcription,
// no matching, no ADC. They sit in Glacier Instant Retrieval until somebody
// promotes one into Live Raw Footage.
//
// The page is deliberately styled in the cool slate-blue accent rather than the
// app's violet, so a user can never be unsure which of the two libraries they
// are looking at.

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { multipartUpload } from '../lib/multipartUpload';
import {
  entriesFromPicker, entriesFromDataTransfer, dropContainsDirectory,
  buildPreflight, formatBytes,
} from '../lib/folderUpload';
import { useAuth } from '../context/AuthContext';
import FrameioMigrationPanel from '../components/FrameioMigrationPanel';
import StorageDownloadModal, { StorageDownloadDock } from '../components/StorageDownloadModal';
import { dismissFinishedDownloads } from '../lib/storageDownload';

const BASE = '/api/storage-archive';
const MAX_PARALLEL_UPLOADS = 3;

// The Storage identity colour. Theme-aware (cool blue in dark, deeper blue in
// light) and visually distinct from Raw Footage's violet.
const ACCENT      = 'var(--s-nr-t)';
const ACCENT_SOFT = 'var(--s-nr)';

// Mirrors CLIENT_ALLOWED_EXTS in the API's lib/storagePolicy.js. Only applied
// for EXTERNAL users — internal uploads are governed server-side by a denylist
// that blocks executables and accepts everything else, so the picker is left
// unfiltered for them. See storagePolicy.js for why the two differ.
//
// This is a HAND-MIRRORED COPY of a server list, and the two drifting is a real
// failure mode: the server is the only enforcement, so a type missing here is
// silently unpickable for clients even though the API would accept it, and a
// type only here is offered and then rejected on submit. tests/acceptParity.test.mjs
// diffs the two files and fails if they disagree. Note .svg is deliberately on
// the list and why it needs care — see storagePolicy.js.
const ACCEPT_EXTS =
  '.mp4,.mov,.avi,.mkv,.m4v,.mxf,.r3d,.braw,.zip,'
  + '.wav,.aif,.aiff,.mp3,.aac,.m4a,.flac,.ogg,'
  + '.pdf,.doc,.docx,.txt,.rtf,.csv,.xlsx,.md,'
  + '.jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif,.avif,.svg,.tif,.tiff,.psd,.ai,.eps,.cube';
const ACCEPT_RE =
  /\.(mp4|mov|avi|mkv|m4v|mxf|r3d|braw|wav|aif|aiff|mp3|aac|m4a|flac|ogg|zip|pdf|docx?|txt|rtf|csv|xlsx|md|jpe?g|png|webp|gif|bmp|heic|heif|avif|svg|tiff?|psd|ai|eps|cube)$/i;

const WRITE_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];

const SOURCE_LABELS = {
  client_upload:       'Project upload',
  internal_upload:     'Internal upload',
  frameio_migration:   'Frame.io migration',
  raw_footage_archive: 'Archived from Raw Footage',
};

// Files that got here by archiving a Raw Footage clip. Worth calling out on the
// tile: the clip they came from is still in Raw Footage with its transcript and
// match history, and Promote is what brings the file back.
const FOOTAGE_ARCHIVE_SOURCE = 'raw_footage_archive';

function FootageArchiveChip({ file, compact = false }) {
  if (file?.source !== FOOTAGE_ARCHIVE_SOURCE) return null;
  return (
    <span
      title="Archived from a Raw Footage clip — Promote copies it back"
      style={{
        fontSize: compact ? 9 : 10, fontWeight: 600, whiteSpace: 'nowrap',
        padding: compact ? '2px 5px' : '2px 7px', borderRadius: 100,
        background: 'rgba(139,109,255,0.14)', color: 'var(--violet)',
        border: '1px solid rgba(139,109,255,0.3)',
      }}
    >❄ {compact ? 'Footage' : 'From Raw Footage'}</span>
  );
}

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function errMsg(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

// The API returns every byte count as a STRING — sums of bigint columns exceed
// what a JSON number holds exactly, and the archive is already ~8 TB. formatBytes
// takes the string happily; this is only for arithmetic on the page.
function bytesNum(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
}

// Live has TWO clients named "TPK". Without this the rollup shows two identical
// rows and reads as a rendering bug rather than as two real client records.
// Only collided names get the suffix, so every other row stays clean.
function disambiguate(rows) {
  const seen = new Map();
  for (const r of rows) seen.set(r.clientName, (seen.get(r.clientName) || 0) + 1);
  return rows.map(r => (seen.get(r.clientName) > 1
    ? { ...r, displayName: `${r.clientName} · ${String(r.clientId).slice(0, 4)}` }
    : { ...r, displayName: r.clientName }));
}

// ─── Grid placeholder ─────────────────────────────────────────────────────────
//
// footage_archive has no thumbnail column today (migration 047 has not landed),
// so `thumbnailUrl` is null on every row. The tile must still look deliberate:
// a flat slot in the page accent carrying the file's extension. NOT a broken
// image, NOT a spinner that never resolves, and NOT an S3 call to go looking —
// browsing this page must never touch S3 (§3.4).
function extOf(name) {
  const m = /\.([A-Za-z0-9]{1,6})$/.exec(name || '');
  return m ? m[1].toUpperCase() : 'FILE';
}

// ─── Folder tree ──────────────────────────────────────────────────────────────

function FolderNode({ node, depth, selectedId, expanded, onToggle, onSelect, onRename, onMove, onDelete, canManage }) {
  const [hover, setHover] = useState(false);
  const isOpen     = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const hasKids    = node.children && node.children.length > 0;

  return (
    <div>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => onSelect(node.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 8px', paddingLeft: 8 + depth * 14,
          borderRadius: 6, cursor: 'pointer', fontSize: 12,
          background: isSelected ? ACCENT_SOFT : hover ? 'var(--bg-hover)' : 'transparent',
          color: isSelected ? ACCENT : 'var(--tx-secondary)',
          fontWeight: isSelected ? 600 : 400,
        }}
      >
        <span
          onClick={e => { e.stopPropagation(); if (hasKids) onToggle(node.id); }}
          style={{ width: 12, flexShrink: 0, fontSize: 9, color: 'var(--tx-faint)', cursor: hasKids ? 'pointer' : 'default' }}
        >{hasKids ? (isOpen ? '▼' : '▶') : ''}</span>
        <span style={{ flexShrink: 0 }}>{isOpen && hasKids ? '📂' : '📁'}</span>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.name}
        </span>
        <span style={{ fontSize: 10, color: 'var(--tx-faint)', flexShrink: 0 }}>{node.fileCount || ''}</span>
        {canManage && hover && (
          <>
            <button
              title="Rename folder"
              onClick={e => { e.stopPropagation(); onRename(node); }}
              style={iconBtn}
            >✎</button>
            {onMove && (
              <button
                title="Move folder…"
                onClick={e => { e.stopPropagation(); onMove(node); }}
                style={iconBtn}
              >⇄</button>
            )}
            <button
              title="Delete folder"
              onClick={e => { e.stopPropagation(); onDelete(node); }}
              style={iconBtn}
            >×</button>
          </>
        )}
      </div>
      {isOpen && hasKids && node.children.map(child => (
        <FolderNode
          key={child.id} node={child} depth={depth + 1}
          selectedId={selectedId} expanded={expanded} onToggle={onToggle} onSelect={onSelect}
          onRename={onRename} onMove={onMove} onDelete={onDelete} canManage={canManage}
        />
      ))}
    </div>
  );
}

const iconBtn = {
  background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer',
  fontSize: 12, padding: '0 3px', lineHeight: 1, flexShrink: 0,
};

// ─── Upload panel ─────────────────────────────────────────────────────────────

// `mode` is 'files' or 'folder'. A folder upload is just a queue of file uploads
// with a resolved destination each — the queue, the 3-parallel cap and the
// cancel/retry/dismiss behaviour are identical to Phase A.
function UploadPanel({ mode, clientId, folderId, folderPath, isExternal, onDone, onClose }) {
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue]       = useState([]);
  const [walking, setWalking]   = useState(null);   // { count } while traversing a tree
  const [preflight, setPreflight] = useState(null); // pending confirmation
  const [notice, setNotice]     = useState(null);
  const fileInput = useRef();
  const activeCount       = useRef(0);
  const pendingQueue      = useRef([]);
  const uploadControllers = useRef({});

  const isFolderMode = mode === 'folder';

  // destFolderId is per item: a folder upload sends each file to the folder that
  // was resolved for its own relative path.
  const runUpload = useCallback(async (id, file, destFolderId) => {
    setQueue(q => q.map(i => i.id === id ? { ...i, state: 'uploading', progress: 0, error: undefined } : i));
    try {
      const upload = multipartUpload({
        file, api,
        endpoints: {
          start:    `${BASE}/upload/multipart/start`,
          signPart: `${BASE}/upload/multipart/sign-part`,
          complete: `${BASE}/upload/multipart/complete`,
          abort:    `${BASE}/upload/multipart/abort`,
        },
        startBody:   { clientId, folderId: destFolderId || null },
        idFieldName: 'archiveId',
        onProgress: (loaded, total) => {
          const pct = Math.round((loaded / total) * 100);
          setQueue(q => q.map(i => i.id === id ? { ...i, progress: pct } : i));
        },
      });
      uploadControllers.current[id] = upload;

      const result = await upload.promise;
      setQueue(q => q.map(i => i.id === id ? { ...i, state: 'verifying', progress: 100 } : i));
      await api.post(`${BASE}/upload/confirm`, { archiveId: result.archiveId });
      setQueue(q => q.map(i => i.id === id ? { ...i, state: 'complete' } : i));
      onDone();
    } catch (err) {
      const isCancel = err?.message === 'cancelled';
      setQueue(q => q.map(i => i.id === id
        ? { ...i, state: isCancel ? 'cancelled' : 'error', error: isCancel ? undefined : errMsg(err, 'Upload failed') }
        : i));
    } finally {
      delete uploadControllers.current[id];
    }
  }, [clientId, onDone]);

  const pumpQueue = useCallback(() => {
    while (activeCount.current < MAX_PARALLEL_UPLOADS && pendingQueue.current.length > 0) {
      const next = pendingQueue.current.shift();
      activeCount.current++;
      runUpload(next.id, next.file, next.destFolderId).finally(() => { activeCount.current--; pumpQueue(); });
    }
  }, [runUpload]);

  // Add already-destined items to the queue.
  const enqueue = useCallback((items) => {
    setQueue(q => [...q, ...items]);
    items.forEach(i => pendingQueue.current.push({ id: i.id, file: i.file, destFolderId: i.destFolderId }));
    pumpQueue();
  }, [pumpQueue]);

  // ── Plain file upload (Phase A behaviour) ─────────────────────────────────
  function handleFiles(files) {
    const all = [...files];
    // External users still face the closed allowlist; internal users are gated
    // server-side by a denylist, so the client filter is only applied for
    // externals. Anything the server rejects surfaces per-file in the queue.
    const accepted = isExternal ? all.filter(f => ACCEPT_RE.test(f.name)) : all;
    if (!accepted.length) {
      setNotice(all.length ? 'None of those file types can be uploaded to your archive.' : null);
      return;
    }
    enqueue(accepted.map(file => ({
      id: `${Date.now()}_${Math.random()}`, file, destFolderId: folderId || null,
      state: 'queued', progress: 0,
    })));
  }

  // ── Folder upload: walk → pre-flight → confirm → resolve → queue ──────────
  const startFolderPreflight = useCallback(async (entries) => {
    const summary = buildPreflight(entries);
    if (!summary.fileCount) {
      setWalking(null);
      setNotice('That folder contains no uploadable files.');
      return;
    }
    setWalking(null);
    try {
      // Dry run: validates depth and the folder caps, and reports what would be
      // created vs merged into — WITHOUT creating anything. This is the gate
      // that stops a big upload dying partway with a half-built tree.
      const { data } = await api.post(`${BASE}/folders/resolve`, {
        clientId, parentId: folderId || null, paths: summary.folderPaths, dryRun: true,
      });
      setPreflight({ summary, dry: data, error: null });
    } catch (err) {
      // A cap or depth breach lands here — show it plainly and block the upload.
      setPreflight({ summary, dry: null, error: errMsg(err, 'Could not validate that folder.') });
    }
  }, [clientId, folderId]);

  const walkAndPreflight = useCallback(async (source) => {
    setNotice(null);
    setWalking({ count: 0 });
    try {
      const entries = source.dataTransfer
        ? await entriesFromDataTransfer(source.dataTransfer, c => setWalking({ count: c }))
        : await entriesFromPicker(source.fileList, c => setWalking({ count: c }));
      if (!entries) {
        // Directory drag isn't supported here — say so and point at the picker
        // rather than failing silently.
        setWalking(null);
        setNotice('This browser can\'t read dropped folders. Use the “Choose folder” button instead.');
        return;
      }
      await startFolderPreflight(entries);
    } catch (err) {
      setWalking(null);
      setNotice(errMsg(err, 'Could not read that folder.'));
    }
  }, [startFolderPreflight]);

  async function confirmFolderUpload() {
    const { summary } = preflight;
    setPreflight(null);
    setNotice(null);
    try {
      // The real resolve. Same request as the dry run, so the counts the user
      // just approved are the counts that happen.
      const { data } = await api.post(`${BASE}/folders/resolve`, {
        clientId, parentId: folderId || null, paths: summary.folderPaths,
      });
      const byPath = new Map(data.resolved.map(r => [r.originalPath.join('/'), r.folderId]));

      enqueue(summary.entries.map(e => ({
        id: `${Date.now()}_${Math.random()}`,
        file: e.file,
        // A file at the root of the dropped folder has no segments and belongs
        // to the destination folder itself.
        destFolderId: e.segments.length ? (byPath.get(e.segments.join('/')) || folderId || null) : (folderId || null),
        relPath: e.segments.join('/'),
        state: 'queued', progress: 0,
      })));
      onDone();
    } catch (err) {
      setNotice(errMsg(err, 'Could not create the folder structure.'));
    }
  }

  function cancelItem(id) {
    uploadControllers.current[id]?.cancel();
    pendingQueue.current = pendingQueue.current.filter(p => p.id !== id);
    setQueue(q => q.map(i => i.id === id && i.state === 'queued' ? { ...i, state: 'cancelled' } : i));
  }

  function retryItem(id) {
    const item = queue.find(i => i.id === id);
    if (!item) return;
    setQueue(q => q.map(i => i.id === id ? { ...i, state: 'queued', progress: 0, error: undefined } : i));
    pendingQueue.current.push({ id, file: item.file, destFolderId: item.destFolderId });
    pumpQueue();
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (isFolderMode || dropContainsDirectory(e.dataTransfer)) {
      walkAndPreflight({ dataTransfer: e.dataTransfer });
    } else {
      handleFiles(e.dataTransfer.files);
    }
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-primary)' }}>
            {isFolderMode ? 'Upload folder into ' : 'Upload to '}
            <span style={{ color: ACCENT }}>{folderPath || 'Unfiled'}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 3 }}>
            Files uploaded here go to cold archive storage. They won&apos;t appear in Raw Footage until promoted.
            {isFolderMode && ' The folder structure you upload is recreated here.'}
          </div>
        </div>
        <button onClick={onClose} style={{ ...iconBtn, fontSize: 16 }}>✕</button>
      </div>

      {notice && (
        <div style={{ background: 'var(--bg-card2)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--tx-secondary)', marginBottom: 10 }}>
          {notice}
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? ACCENT : 'var(--border-mid)'}`,
          borderRadius: 12, padding: '22px 20px', textAlign: 'center', cursor: 'pointer',
          background: dragOver ? ACCENT_SOFT : 'var(--bg-card2)', transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 22, marginBottom: 6 }}>{isFolderMode ? '🗂' : '▤'}</div>
        <div style={{ fontSize: 13, color: 'var(--tx-secondary)', fontWeight: 600 }}>
          {isFolderMode ? 'Drop a folder here, or click to choose folder' : 'Drop files here or click to browse'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
          {isFolderMode
            ? 'Sub-folders are recreated. System files like .DS_Store are skipped.'
            : (isExternal
                ? 'Video, audio, stills, documents and archives'
                : 'Any project file except executables')}
        </div>
        {/* webkitdirectory is the primary path — consistent across browsers. */}
        <input
          ref={fileInput} type="file" multiple style={{ display: 'none' }}
          {...(isFolderMode ? { webkitdirectory: '', directory: '' } : { accept: isExternal ? ACCEPT_EXTS : undefined })}
          onChange={e => {
            if (isFolderMode) walkAndPreflight({ fileList: e.target.files });
            else handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {walking && (
        <div style={{ marginTop: 10, fontSize: 12, color: ACCENT }}>
          ◌ Reading folder… {walking.count} file{walking.count === 1 ? '' : 's'} found
        </div>
      )}

      {preflight && (
        <PreflightDialog
          preflight={preflight}
          destination={folderPath || 'Unfiled'}
          onCancel={() => setPreflight(null)}
          onConfirm={confirmFolderUpload}
        />
      )}

      {queue.length > 0 && (() => {
        const done     = queue.filter(i => i.state === 'complete').length;
        const failed   = queue.filter(i => i.state === 'error').length;
        const cancelled = queue.filter(i => i.state === 'cancelled').length;
        const active   = queue.length - done - failed - cancelled;
        return (
        <>
        {/* Pinned summary — sits OUTSIDE the scroller so it stays put while the
            list scrolls. With several hundred queued files the list alone tells
            you nothing about overall state. Derived from queue state only; it
            drives nothing. */}
        <div style={{
          marginTop: 12, marginBottom: 6, display: 'flex', alignItems: 'baseline',
          gap: 8, flexWrap: 'wrap', fontSize: 11, color: 'var(--tx-muted)',
        }}>
          <span style={{ color: 'var(--tx-secondary)', fontWeight: 600 }}>
            {done} of {queue.length} archived
          </span>
          {active > 0     && <span>· {active} in progress</span>}
          {failed > 0     && <span style={{ color: 'var(--s-incomplete-t)' }}>· {failed} failed</span>}
          {cancelled > 0  && <span>· {cancelled} cancelled</span>}
        </div>

        {/* The list is the ONLY thing that scrolls here. Capped against the
            viewport as well as an absolute value so it stays usable on a short
            window; `min()` keeps one file compact (max-height, not height) and
            several hundred bounded. */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 6,
          maxHeight: 'min(340px, 45vh)', overflowY: 'auto',
          // Stops a flick at the end of the list scrolling the page behind it.
          overscrollBehavior: 'contain',
          paddingRight: 4,
        }}>
          {queue.map(item => (
            // flexShrink: 0 — in a column flex container with a constrained
            // height, rows would otherwise compress into unreadable slivers
            // instead of overflowing into the scroller.
            <div key={item.id} style={{ flexShrink: 0, background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</div>
                <div style={{ fontSize: 11, color: item.state === 'complete' ? 'var(--s-approved-t)' : item.state === 'error' ? 'var(--s-incomplete-t)' : 'var(--tx-muted)' }}>
                  {item.state === 'queued'    ? 'Queued…' :
                   item.state === 'uploading' ? `Uploading ${item.progress}%` :
                   item.state === 'verifying' ? 'Verifying…' :
                   item.state === 'complete'  ? '✓ Archived' :
                   item.state === 'cancelled' ? 'Cancelled' :
                   (item.error || 'Upload failed')}
                </div>
              </div>
              {item.state === 'uploading' && (
                <div style={{ width: 80, height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{ height: '100%', width: `${item.progress}%`, background: ACCENT, borderRadius: 2, transition: 'width 0.2s' }} />
                </div>
              )}
              {(item.state === 'uploading' || item.state === 'queued') && (
                <button onClick={() => cancelItem(item.id)} style={smallBtn('var(--border)', 'var(--tx-muted)')}>Cancel</button>
              )}
              {(item.state === 'error' || item.state === 'cancelled') && (
                <button onClick={() => retryItem(item.id)} style={smallBtn(ACCENT, ACCENT)}>Retry</button>
              )}
              <button onClick={() => setQueue(q => q.filter(i => i.id !== item.id))} style={iconBtn}>✕</button>
            </div>
          ))}
        </div>
        </>
        );
      })()}
    </div>
  );
}

// ─── Pre-flight dialog ────────────────────────────────────────────────────────
//
// No folder upload starts without this. It exists because the depth and folder
// caps reject at CREATION time — without an up-front dry run, a large upload can
// die partway and leave a half-built tree behind.
//
// A merge is never silent: if any existing folder is being added to, that is
// stated in plain words, not left to be inferred from a number.
function PreflightDialog({ preflight, destination, onCancel, onConfirm }) {
  const { summary, dry, error } = preflight;
  const blocked = !!error;
  const merging = dry ? dry.existingCount > 0 : false;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 80,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--bg-card)', border: `1px solid ${blocked ? 'var(--s-incomplete-t)' : ACCENT}`,
        borderRadius: 14, padding: 20, width: 'min(520px, 100%)', maxHeight: '80vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 4 }}>
          {blocked ? 'This folder can’t be uploaded' : 'Confirm folder upload'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--tx-muted)', marginBottom: 14 }}>
          Destination: <span style={{ color: ACCENT, fontWeight: 600 }}>{destination}</span>
        </div>

        {blocked ? (
          <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '10px 13px', fontSize: 12, lineHeight: 1.5 }}>
            {error}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 14, color: 'var(--tx-primary)', fontWeight: 600 }}>
              {summary.fileCount.toLocaleString()} file{summary.fileCount === 1 ? '' : 's'} across{' '}
              {summary.folderCount.toLocaleString()} folder{summary.folderCount === 1 ? '' : 's'} ·{' '}
              {formatBytes(summary.totalBytes)}
            </div>

            <div style={{ fontSize: 13, color: 'var(--tx-secondary)' }}>
              Creates <strong style={{ color: ACCENT }}>{dry.createdCount}</strong> new folder{dry.createdCount === 1 ? '' : 's'}
              {' · '}adds to <strong style={{ color: merging ? 'var(--s-filmed-t)' : 'var(--tx-secondary)' }}>{dry.existingCount}</strong> existing
            </div>

            {merging && (
              <div style={{ background: 'var(--s-filmed)', color: 'var(--s-filmed-t)', border: '1px solid var(--s-filmed-t)', borderRadius: 8, padding: '9px 12px', fontSize: 12, lineHeight: 1.5 }}>
                ⚠ Some of these files will be added to folders that already exist. If you meant to
                create a fresh folder, cancel and check the destination first.
              </div>
            )}

            {dry.sanitized?.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--tx-muted)' }}>
                <div style={{ marginBottom: 4 }}>
                  {dry.sanitized.length} folder name{dry.sanitized.length === 1 ? '' : 's'} will be cleaned up:
                </div>
                <div style={{ maxHeight: 110, overflowY: 'auto', fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {dry.sanitized.slice(0, 25).map((s, i) => (
                    <div key={i}>
                      <span style={{ color: 'var(--tx-faint)', textDecoration: 'line-through' }}>{s.from}</span>
                      {' → '}
                      <span style={{ color: 'var(--tx-secondary)' }}>{s.to}</span>
                    </div>
                  ))}
                  {dry.sanitized.length > 25 && <div>…and {dry.sanitized.length - 25} more</div>}
                </div>
              </div>
            )}

            {summary.skippedJunk > 0 && (
              <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>
                {summary.skippedJunk} system file{summary.skippedJunk === 1 ? '' : 's'} skipped
                (.DS_Store, Thumbs.db and similar)
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ ...smallBtn('var(--border-mid)', 'var(--tx-secondary)'), fontSize: 12, padding: '7px 14px' }}>
            {blocked ? 'Close' : 'Cancel'}
          </button>
          {!blocked && (
            <button onClick={onConfirm} style={{ ...smallBtn(ACCENT, ACCENT), fontSize: 12, fontWeight: 600, padding: '7px 14px' }}>
              Upload {summary.fileCount.toLocaleString()} file{summary.fileCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Mirrors mayDeleteFile() in the API's routes/storageArchive.js. It has to be a
// function of the FILE, not of the page, because the uploader half of the rule
// is per-row: you may remove the file you archived yourself and nothing else.
// The old page-level canDelete was Admin/Power-User only, which is why the API
// had allowed an uploader to delete their own file since Build A Part 2 while
// the button that would do it was hidden from them.
//
// External client users are included as of 2026-08-27 (Owner) — own uploads
// only, never the admin branch, never a colleague's file on the same client.
//
// Keep the two in step. The server is the enforcement; this only decides
// whether the control is worth showing.
function mayDeleteFile(perm, file) {
  if (!perm) return false;
  const isOwnUpload = !!file?.uploadedBy && file.uploadedBy === perm.userId;
  if (perm.isExternal) return isOwnUpload;
  return perm.isAdmin || isOwnUpload;
}

function smallBtn(borderColor, color) {
  return {
    background: 'none', border: `1px solid ${borderColor}`, color,
    cursor: 'pointer', fontSize: 11, padding: '2px 8px', borderRadius: 6, flexShrink: 0, lineHeight: 1.4,
  };
}

// ─── Detail drawer ────────────────────────────────────────────────────────────

function DetailDrawer({ file, folders, canPromote, canWrite, deletePerm, retention, onClose, onChanged }) {
  // The global light/dark toggle is position:fixed with a HIGHER z-index than
  // this drawer, so it floated over the panel (owner report 2026-09-02). Hide
  // it while the drawer is open; restore on close.
  useEffect(() => {
    const el = document.querySelector('.theme-toggle');
    if (el) el.style.visibility = 'hidden';
    return () => { if (el) el.style.visibility = ''; };
  }, []);

  const [notes, setNotes]       = useState(file.notes || '');
  const [saving, setSaving]     = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [live, setLive]         = useState(file);
  const [choosingDest, setChoosingDest] = useState(false);
  // Rename (owner order 2026-09-02): display name only, the stored object
  // never changes. null = not editing.
  const [renaming, setRenaming] = useState(null);
  // Cross-client move (Admin/Power User): active clients for the picker.
  const [moveClients, setMoveClients] = useState([]);

  useEffect(() => { setLive(file); setNotes(file.notes || ''); setError(null); setRenaming(null); }, [file]);
  useEffect(() => {
    if (!deletePerm.isAdmin) return;
    api.get('/api/clients').then(r => setMoveClients(r.data || [])).catch(() => {});
  }, [deletePerm.isAdmin]);

  // While a promote is running, poll the file endpoint so the chip flips to
  // ✓ Promoted without a manual refresh.
  useEffect(() => {
    if (live.promoteStatus !== 'promoting') return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`${BASE}/files/${live.id}`);
        setLive(data);
        if (data.promoteStatus !== 'promoting') { onChanged(); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [live.promoteStatus, live.id, onChanged]);

  async function saveNotes() {
    setSaving(true); setError(null);
    try {
      const { data } = await api.patch(`${BASE}/files/${live.id}`, { notes });
      setLive(data); onChanged();
    } catch (err) { setError(errMsg(err, 'Could not save notes.')); }
    finally { setSaving(false); }
  }

  async function move(folderId) {
    setBusy(true); setError(null);
    try {
      const { data } = await api.patch(`${BASE}/files/${live.id}`, { folderId: folderId || null });
      setLive(data); onChanged();
    } catch (err) { setError(errMsg(err, 'Could not move the file.')); }
    finally { setBusy(false); }
  }

  async function saveRename() {
    const name = (renaming || '').trim();
    if (!name || name === live.fileName) { setRenaming(null); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.patch(`${BASE}/files/${live.id}`, { fileName: name });
      setLive(data); setRenaming(null); onChanged();
    } catch (err) { setError(errMsg(err, 'Could not rename the file.')); }
    finally { setBusy(false); }
  }

  async function moveToClient(clientId) {
    if (!clientId || clientId === live.clientId) return;
    const target = moveClients.find(c => c.id === clientId);
    if (!window.confirm(`Move "${live.fileName}" to ${target?.name || 'that project'}? It lands in their Unfiled root and leaves this project's storage.`)) return;
    setBusy(true); setError(null);
    try {
      await api.patch(`${BASE}/files/${live.id}`, { clientId });
      onChanged();
      onClose(); // the file no longer belongs to the client being browsed
    } catch (err) { setError(errMsg(err, 'Could not move the file.')); setBusy(false); }
  }

  // Download no longer fires straight at the browser's default folder. The
  // modal reads the destination machine — what it can do, what room it reports,
  // which folders this user has used before and what is already in the one they
  // choose — and only then starts a streamed, resumable transfer. The transfer
  // itself is owned by the job registry, so it survives this drawer closing.
  function download() {
    setError(null);
    setChoosingDest(true);
  }

  async function promote() {
    setConfirming(false); setBusy(true); setError(null);
    setLive(l => ({ ...l, promoteStatus: 'promoting' }));
    try {
      await api.post(`${BASE}/files/${live.id}/promote`);
      const { data } = await api.get(`${BASE}/files/${live.id}`);
      setLive(data); onChanged();
    } catch (err) {
      setError(errMsg(err, 'Promote failed.'));
      try {
        const { data } = await api.get(`${BASE}/files/${live.id}`);
        setLive(data);
      } catch { setLive(l => ({ ...l, promoteStatus: 'failed' })); }
    } finally { setBusy(false); }
  }

  async function softDelete() {
    // The old copy said the file "is kept in cold storage — this only hides it
    // from the list". With the reaper armed that is no longer true, and this
    // dialog is the only place anyone is told their file will be destroyed. The
    // window comes from the server (STORAGE_DELETE_GRACE_DAYS) rather than a
    // literal, so changing the env cannot turn this message into a lie.
    const days   = retention?.graceDays ?? null;
    const armed  = retention?.reaperArmed === true;
    const body = armed && days
      ? `It disappears from Storage straight away.\n\n`
        + `The file itself is then permanently deleted from cold storage after ${days} days — `
        + `no sooner, and a little later for very recently uploaded files. `
        + `Only an Admin can restore it, and only inside that window.\n\n`
        + `Once the ${days} days are up this cannot be undone.`
      : `It disappears from Storage straight away. The file itself is kept in cold `
        + `storage and can be restored by an Admin.`;
    if (!window.confirm(`Delete "${live.fileName}" from Storage?\n\n${body}`)) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`${BASE}/files/${live.id}`);
      onChanged(); onClose();
    } catch (err) { setError(errMsg(err, 'Could not delete.')); setBusy(false); }
  }

  // Flatten the tree for the "move to" picker.
  const flat = [];
  (function walk(nodes, depth) {
    for (const n of nodes) { flat.push({ ...n, depth }); walk(n.children || [], depth + 1); }
  })(folders, 0);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 100vw)',
      background: 'var(--bg-card)', borderLeft: `1px solid ${ACCENT}`,
      boxShadow: 'var(--shadow-lg)', zIndex: 60, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase', color: ACCENT, fontWeight: 700 }}>Cold archive</div>
          {renaming !== null ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <input
                autoFocus
                value={renaming}
                disabled={busy}
                onChange={e => setRenaming(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                style={{ flex: 1, minWidth: 0, background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 6, padding: '5px 8px', color: 'var(--tx-primary)', fontSize: 13, fontFamily: 'var(--font)' }}
              />
              <button onClick={saveRename} disabled={busy} style={{ ...iconBtn, fontSize: 13 }} title="Save name">✓</button>
              <button onClick={() => setRenaming(null)} disabled={busy} style={{ ...iconBtn, fontSize: 13 }} title="Cancel">✕</button>
            </div>
          ) : (
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)', wordBreak: 'break-word', marginTop: 3, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ minWidth: 0 }}>{live.fileName}</span>
              {canWrite && (
                <button onClick={() => setRenaming(live.fileName)} style={{ ...iconBtn, fontSize: 12, flexShrink: 0 }} title="Rename file">✎</button>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{ ...iconBtn, fontSize: 18 }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
            {error}
          </div>
        )}

        <PromoteChip file={live} />

        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '7px 14px', fontSize: 12 }}>
          <Meta label="Folder"       value={live.folderPath || 'Unfiled'} />
          <Meta label="Project"       value={live.clientName || '—'} />
          <Meta label="Size"         value={formatBytes(live.fileSizeBytes)} />
          <Meta label="Storage class" value={live.storageClass} />
          <Meta label="Source"       value={SOURCE_LABELS[live.source] || live.source} />
          <Meta label="Uploaded by"  value={live.uploadedByName || '—'} />
          <Meta label="Uploaded"     value={fmtDate(live.createdAt)} />
        </dl>

        <div>
          <label style={fieldLabel}>Notes</label>
          <textarea
            value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)', resize: 'vertical' }}
          />
          {canWrite && (
            <button onClick={saveNotes} disabled={saving || notes === (live.notes || '')} style={{ ...smallBtn('var(--border-mid)', 'var(--tx-secondary)'), marginTop: 6 }}>
              {saving ? 'Saving…' : 'Save notes'}
            </button>
          )}
        </div>

        {canWrite && (
          <div>
            <label style={fieldLabel}>Move to folder…</label>
            <select
              value={live.folderId || ''} disabled={busy}
              onChange={e => move(e.target.value)}
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)' }}
            >
              <option value="">Unfiled (project root)</option>
              {flat.map(f => (
                <option key={f.id} value={f.id}>{' '.repeat(f.depth * 3)}{f.name}</option>
              ))}
            </select>
          </div>
        )}

        {deletePerm.isAdmin && moveClients.length > 0 && (
          <div>
            <label style={fieldLabel}>Move to project…</label>
            <select
              value={live.clientId || ''} disabled={busy}
              onChange={e => moveToClient(e.target.value)}
              title="Moves this file into another project's storage (lands in their Unfiled root)"
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)' }}
            >
              {!moveClients.some(c => c.id === live.clientId) && (
                <option value={live.clientId || ''}>{live.clientName || 'Unassigned'}</option>
              )}
              {moveClients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={download} style={primaryBtn('var(--border-mid)', 'var(--tx-primary)')}>⬇ Download</button>

          {/* Promote is video-only: it inserts into raw_footage and queues a
              transcode, which is meaningless for a PDF or a still. The control
              is hidden rather than shown-and-disabled, with a short note so the
              absence is explained instead of just puzzling. */}
          {canPromote && live.promoteStatus !== 'promoted' && live.promotable && (
            <button
              onClick={() => setConfirming(true)}
              disabled={busy || live.promoteStatus === 'promoting' || live.uploadStatus !== 'complete'}
              style={primaryBtn(ACCENT, ACCENT)}
            >
              {live.promoteStatus === 'promoting' ? '◌ Promoting…' : '↑ Promote to Live Footage'}
            </button>
          )}

          {canPromote && !live.promotable && (
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.5, padding: '2px 2px 0' }}>
              Only video files can be promoted to Live footage. This file stays in the archive and
              can be downloaded from here.
            </div>
          )}

          {mayDeleteFile(deletePerm, live) && (
            <>
              <button onClick={softDelete} disabled={busy} style={primaryBtn('var(--border)', 'var(--s-incomplete-t)')}>
                {retention?.reaperArmed && retention?.graceDays
                  ? `Delete from Storage (${retention.graceDays}-day recovery)`
                  : 'Remove from Storage index'}
              </button>
              {!deletePerm.isAdmin && (
                <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.5, padding: '2px 2px 0' }}>
                  You can remove this one because you uploaded it. Anything uploaded by
                  someone else can only be removed by an Admin.
                </div>
              )}
            </>
          )}
        </div>

        {choosingDest && (
          <StorageDownloadModal
            file={live}
            api={api}
            base={BASE}
            onClose={() => setChoosingDest(false)}
          />
        )}

        {confirming && (
          <div style={{ background: 'var(--bg-card2)', border: `1px solid ${ACCENT}`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.5 }}>
              This copies the file into Live Raw Footage where it can be transcribed, matched and
              processed. The archive copy is kept.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={promote} style={primaryBtn(ACCENT, ACCENT)}>Promote</button>
              <button onClick={() => setConfirming(false)} style={primaryBtn('var(--border)', 'var(--tx-muted)')}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <>
      <dt style={{ color: 'var(--tx-faint)' }}>{label}</dt>
      <dd style={{ margin: 0, color: 'var(--tx-secondary)', wordBreak: 'break-word' }}>{value}</dd>
    </>
  );
}

const fieldLabel = { display: 'block', fontSize: 11, color: 'var(--tx-faint)', marginBottom: 5 };

function primaryBtn(borderColor, color) {
  return {
    width: '100%', background: 'none', border: `1px solid ${borderColor}`, color,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '9px 12px',
    borderRadius: 8, fontFamily: 'var(--font)',
  };
}

// Status chip: Archived (slate) or ✓ Promoted (green, linking to the Live row).
function PromoteChip({ file, compact }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
    padding: compact ? '2px 8px' : '4px 10px', borderRadius: 100, whiteSpace: 'nowrap',
  };
  if (file.promoteStatus === 'promoted') {
    return (
      <Link
        to={`/footage?footage=${file.promotedFootageId}`}
        onClick={e => e.stopPropagation()}
        style={{ ...base, background: 'var(--s-approved)', color: 'var(--s-approved-t)', textDecoration: 'none' }}
        title="Open the Live Raw Footage row"
      >✓ Promoted</Link>
    );
  }
  if (file.promoteStatus === 'promoting') {
    return <span style={{ ...base, background: ACCENT_SOFT, color: ACCENT }}>◌ Promoting…</span>;
  }
  if (file.promoteStatus === 'failed') {
    return <span style={{ ...base, background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)' }} title={file.promoteError || ''}>⚠ Promote failed</span>;
  }
  if (file.uploadStatus === 'failed') {
    return <span style={{ ...base, background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)' }}>⚠ Upload failed</span>;
  }
  if (file.uploadStatus === 'pending') {
    return <span style={{ ...base, background: 'var(--s-scripted)', color: 'var(--s-scripted-t)' }}>◌ Uploading</span>;
  }
  return <span style={{ ...base, background: 'var(--s-scripted)', color: 'var(--s-scripted-t)' }}>Archived</span>;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

// ─── Client rollup — the landing state ────────────────────────────────────────
//
// What is in storage, across every client the caller can see, before a client is
// picked. Sorted by size descending with the aggregate on the header line.
//
// Clients with nothing in storage are returned by the API (zeros, never a 404)
// but folded away behind a disclosure: on live 23 of 30 rows are empty, and a
// landing screen that is three-quarters zeros hides the seven rows that matter.
function ClientRollup({ summary, onPick }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const rows    = disambiguate(summary.clients || []);
  const totals  = summary.totals || {};
  const active  = rows.filter(r => r.fileCount > 0 || r.folderCount > 0);
  const empties = rows.filter(r => !(r.fileCount > 0 || r.folderCount > 0));

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-primary)' }}>All projects</span>
        <span style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>
          {totals.clientsWithContent || 0} with content · {(totals.fileCount || 0).toLocaleString()} assets · {formatBytes(totals.totalBytes)}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 130px 130px', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--border)', fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--tx-faint)', fontWeight: 700 }}>
        <div>Project</div><div>Folders</div><div>Assets</div><div>Size</div><div>Last upload</div>
      </div>

      {active.length === 0 ? (
        <EmptyState bare title="Nothing archived yet" body="No project has anything in cold storage." />
      ) : active.map(r => (
        <div
          key={r.clientId}
          onClick={() => onPick(r.clientId)}
          style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 130px 130px', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--tx-secondary)', cursor: 'pointer', alignItems: 'center' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ color: ACCENT }}>▤</span>
            <span style={{ color: 'var(--tx-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.displayName}</span>
          </div>
          <div>{r.folderCount.toLocaleString()}</div>
          <div>{r.fileCount.toLocaleString()}</div>
          <div style={{ color: 'var(--tx-primary)', fontWeight: 500 }}>{formatBytes(r.totalBytes)}</div>
          <div style={{ color: 'var(--tx-muted)' }}>{fmtDate(r.lastUploadAt)}</div>
        </div>
      ))}

      {empties.length > 0 && (
        <div style={{ padding: '10px 16px' }}>
          <button
            onClick={() => setShowEmpty(v => !v)}
            style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font)', padding: 0 }}
          >
            {showEmpty ? '▼' : '▶'} {empties.length} project{empties.length === 1 ? '' : 's'} with nothing in storage
          </button>
          {showEmpty && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {empties.map(r => (
                <button
                  key={r.clientId}
                  onClick={() => onPick(r.clientId)}
                  style={{ background: 'none', border: '1px solid var(--border-mid)', borderRadius: 6, color: 'var(--tx-muted)', cursor: 'pointer', fontSize: 11, padding: '4px 9px', fontFamily: 'var(--font)' }}
                >{r.displayName}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Folder cards ─────────────────────────────────────────────────────────────
//
// A card reports what is inside that folder ALL THE WAY DOWN — that is what
// makes it worth reading. The header's asset line above reports only what the
// current folder holds directly. Two different measures on one screen, so they
// are labelled differently and never share a word.
function FolderCards({ folders, onOpen }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, padding: 14, borderBottom: '1px solid var(--border)' }}>
      {folders.map(f => (
        <div
          key={f.id}
          onClick={() => onOpen(f.id)}
          style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: 'var(--bg-input)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          {/* Reserves the area a thumbnail collage will fill. One neutral folder
              tile until there is something to put in it. */}
          <div style={{ aspectRatio: '16 / 9', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, color: ACCENT }}>
            📁
          </div>
          <div style={{ padding: '9px 11px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 3 }}>
              {f.folderCount > 0 && <>{f.folderCount} folder{f.folderCount === 1 ? '' : 's'} · </>}
              {f.fileCount.toLocaleString()} inside · {formatBytes(f.totalBytes)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Grid view ────────────────────────────────────────────────────────────────
function FileGrid({ files, onSelect }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, padding: 14 }}>
      {files.map(f => (
        <div
          key={f.id}
          onClick={() => onSelect(f)}
          style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: 'var(--bg-input)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
        >
          <div style={{ aspectRatio: '16 / 9', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {f.thumbnailUrl
              ? <img src={f.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, color: ACCENT, opacity: 0.85 }}>{extOf(f.fileName)}</span>}
            <div style={{ position: 'absolute', top: 6, right: 6 }}><PromoteChip file={f} compact /></div>
            <div style={{ position: 'absolute', top: 6, left: 6 }}><FootageArchiveChip file={f} compact /></div>
          </div>
          <div style={{ padding: '9px 11px' }}>
            <div title={f.fileName} style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 3 }}>{formatBytes(f.fileSizeBytes)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Storage() {
  const { user } = useAuth();
  const isExternal = !!user?.isExternal;

  const canWrite = isExternal
    ? !!user?.assignedClientIds?.length
    : (user?.roles || []).some(r => WRITE_ROLES.includes(r));
  const canPromote = !isExternal && (user?.roles || []).some(r => WRITE_ROLES.includes(r));
  const isAdmin    = !isExternal && (user?.roles || []).some(r => ['Admin', 'Power User', 'Power_User'].includes(r));
  // Delete is Admin OR the file's own uploader, internal only — see
  // mayDeleteFile above and the matching gate in the API.
  const deletePerm = { isExternal, isAdmin, userId: user?.id || null };
  // The Frame.io migration is Admin-only and internal-only. Matches the server
  // gate in routes/frameio.js exactly — the UI hides it, the API refuses it.
  const canMigrate = !isExternal && (user?.roles || []).some(r => ['Admin', 'Power User', 'Power_User'].includes(r));

  const [clients,  setClients]  = useState([]);
  const [tree, setTree]         = useState([]);
  const [counts, setCounts]     = useState({ unfiledCount: 0, totalCount: 0 });

  // ─── Where you are lives in the URL, not in React state ─────────────────────
  //
  // clientId and folderId are read FROM the query string rather than mirrored
  // into it, so there is exactly one source of truth and no way for the two to
  // drift out of step. A pasted or bookmarked URL is therefore not a special
  // case needing its own code path — it is the ONLY case, and the ordinary
  // render path already handles it.
  //
  // The list/grid toggle deliberately does NOT live here. It is a preference,
  // not a location: it belongs to the user across every folder they visit, and
  // it stays in localStorage.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';
  const selected = searchParams.get('folderId') || 'all';   // 'all' | 'unfiled' | folderId

  // Every move the USER makes pushes a history entry, so Back retraces the path
  // one folder at a time and Forward replays it.
  //
  // Moves the PAGE makes replace instead — landing a single-client user,
  // resolving a ?fileId= deep link, recovering from a folder that was just
  // deleted. None of those is a place the user chose to be, and a Back that
  // returned to one would either be a dead end or would silently re-run the
  // navigation that left it.
  const go = useCallback((next, { replace = false } = {}) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      // fileId is consumed on arrival and never carried forward: leaving it in
      // would re-open the drawer on every subsequent navigation.
      p.delete('fileId');
      if ('client' in next) { if (next.client) p.set('clientId', next.client); else p.delete('clientId'); }
      if ('folder' in next) { if (next.folder) p.set('folderId', next.folder); else p.delete('folderId'); }
      return p;
    }, { replace });
  }, [setSearchParams]);

  const openFolder = useCallback((id, opts) => go({ folder: id }, opts), [go]);
  const pickClient = useCallback((id) => go({ client: id, folder: 'all' }), [go]);
  const toRollup   = useCallback(() => go({ client: null, folder: null }), [go]);
  const [expanded, setExpanded] = useState(new Set());
  const [files, setFiles]       = useState([]);
  const [retention, setRetention] = useState(null);
  // Bulk-delete selection (list view). Pruned to visible files on every fetch
  // so a hidden row can never be silently mass-deleted.
  const [selectedFiles,  setSelectedFiles]  = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  useEffect(() => {
    setSelectedFiles(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(files.map(f => f.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [files]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [detail, setDetail]     = useState(null);
  const [showUpload, setShowUpload] = useState(null);   // null | 'files' | 'folder'
  const PAGE_SIZE = 50;

  // The client rollup, and the folder-card payload for wherever we are standing.
  const [summary, setSummary] = useState(null);
  const [browse,  setBrowse]  = useState(null);   // { folder, children, assets }

  // List vs grid. Persisted per user in localStorage, defaulting to list, and
  // it changes only how ASSETS render — folder cards are unaffected.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('storageViewMode') === 'grid' ? 'grid' : 'list'; }
    catch { return 'list'; }
  });
  useEffect(() => {
    try { localStorage.setItem('storageViewMode', viewMode); } catch { /* private mode */ }
  }, [viewMode]);

  // Deep link from a Live footage row ("From Storage archive"): open that file's
  // drawer and switch the browser to its client.
  // The raw ?fileId= URL is REPLACED by the location it resolves to, in a single
  // navigation. Back must never return to it: arriving there again would re-run
  // this fetch and re-open the drawer the user had just closed.
  useEffect(() => {
    const fileId = searchParams.get('fileId');
    if (!fileId) return;
    api.get(`${BASE}/files/${fileId}`)
      .then(({ data }) => {
        setDetail(data);
        go({ client: data.clientId, folder: data.folderId || 'unfiled' }, { replace: true });
      })
      .catch(() => {
        setError('That archived file is no longer available.');
        go({}, { replace: true });   // drop the fileId, stay where we are
      });
  }, [searchParams, go]);

  // Internal users pick a client; external users are pinned to their own.
  useEffect(() => {
    if (isExternal) return;
    api.get('/api/clients')
      .then(({ data }) => setClients(Array.isArray(data) ? data : (data.rows || [])))
      .catch(() => setClients([]));
  }, [isExternal]);

  // The client rollup. Loaded for every user, not just internal ones: it is also
  // how a single-client user is landed straight in their own tree. The old
  // auto-set read assignedClientIds out of the JWT — this reads the set the
  // server actually scoped, which is the same rule every route here follows.
  useEffect(() => {
    api.get(`${BASE}/summary`)
      .then(({ data }) => setSummary(data))
      .catch(err => setError(errMsg(err, 'Could not load the storage summary.')));
  }, []);

  // A caller scoped to exactly one client has no rollup worth looking at, so
  // land them in their own tree — REPLACING, so Back leaves the page rather than
  // bouncing them into a rollup they were never shown. Skipped entirely when the
  // URL already names a client, or while a ?fileId= deep link is resolving.
  useEffect(() => {
    if (!summary || clientId || searchParams.get('fileId')) return;
    if ((summary.clients || []).length === 1) {
      go({ client: summary.clients[0].clientId, folder: 'all' }, { replace: true });
    }
  }, [summary, clientId, searchParams, go]);

  // A pasted URL must arrive with the sidebar already expanded to the folder it
  // names. The breadcrumb IS that ancestor chain, so expanding it is exact — no
  // second walk of the tree, and it works even before the tree has loaded.
  useEffect(() => {
    const crumbs = browse?.folder?.breadcrumb || [];
    if (!crumbs.length) return;
    setExpanded(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const c of crumbs) if (!next.has(c.id)) { next.add(c.id); changed = true; }
      return changed ? next : prev;   // same Set when nothing moved, or this loops
    });
  }, [browse]);

  // Folder cards + breadcrumb for wherever we are standing. 'all' and 'unfiled'
  // both stand at the client root, where the cards are the top-level folders.
  const loadBrowse = useCallback(async () => {
    if (!clientId) { setBrowse(null); return; }
    const atRoot = selected === 'all' || selected === 'unfiled';
    try {
      const { data } = await api.get(`${BASE}/folders/${atRoot ? 'root' : selected}/children`,
        atRoot ? { params: { clientId } } : undefined);
      setBrowse(data);
    } catch {
      // A folder that has just been deleted or is out of scope answers 404 —
      // the cards simply disappear; the file list surfaces its own error.
      setBrowse(null);
    }
  }, [clientId, selected]);
  useEffect(() => { loadBrowse(); }, [loadBrowse]);

  const loadTree = useCallback(async () => {
    if (!clientId) { setTree([]); setCounts({ unfiledCount: 0, totalCount: 0 }); return; }
    try {
      const { data } = await api.get(`${BASE}/folders`, { params: { clientId } });
      setTree(data.folders || []);
      setCounts({ unfiledCount: data.unfiledCount || 0, totalCount: data.totalCount || 0 });
    } catch (err) { setError(errMsg(err, 'Could not load folders.')); }
  }, [clientId]);

  const loadFiles = useCallback(async () => {
    if (!clientId) { setFiles([]); setTotal(0); return; }
    setLoading(true); setError(null);
    try {
      const params = { clientId, page, pageSize: PAGE_SIZE };
      if (selected === 'all')          params.folderId = 'all';
      else if (selected !== 'unfiled') params.folderId = selected;
      if (search.trim())               params.search = search.trim();
      const { data } = await api.get(`${BASE}/files`, { params });
      setFiles(data.rows || []);
      setTotal(data.total || 0);
      if (data.retention) setRetention(data.retention);
    } catch (err) { setError(errMsg(err, 'Could not load files.')); setFiles([]); }
    finally { setLoading(false); }
  }, [clientId, selected, search, page]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => {
    const t = setTimeout(loadFiles, search ? 250 : 0);   // debounce typing
    return () => clearTimeout(t);
  }, [loadFiles, search]);
  useEffect(() => { setPage(1); }, [selected, search, clientId]);

  const refresh = useCallback(() => { loadTree(); loadFiles(); loadBrowse(); }, [loadTree, loadFiles, loadBrowse]);

  function toggle(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function newFolder() {
    const name = window.prompt('New folder name:');
    if (!name?.trim()) return;
    const parentId = (selected === 'all' || selected === 'unfiled') ? null : selected;
    try {
      await api.post(`${BASE}/folders`, { clientId, parentId, name: name.trim() });
      if (parentId) setExpanded(prev => new Set(prev).add(parentId));
      loadTree();
    } catch (err) { window.alert(errMsg(err, 'Could not create the folder.')); }
  }

  async function renameFolder(node) {
    const name = window.prompt('Rename folder:', node.name);
    if (!name?.trim() || name.trim() === node.name) return;
    try {
      await api.patch(`${BASE}/folders/${node.id}`, { name: name.trim() });
      refresh();
    } catch (err) { window.alert(errMsg(err, 'Could not rename the folder.')); }
  }

  // Recycle bin (owner order 2026-09-02): deleted files in the grace window,
  // restorable in place. Data comes from GET /files/deleted.
  const [recycleOpen,    setRecycleOpen]    = useState(false);
  const [recycleRows,    setRecycleRows]    = useState([]);
  const [recycleGrace,   setRecycleGrace]   = useState(null);
  const [recycleLoading, setRecycleLoading] = useState(false);
  const [restoringId,    setRestoringId]    = useState(null);
  const loadRecycle = useCallback(async () => {
    setRecycleLoading(true);
    try {
      const params = clientId ? `?clientId=${clientId}` : '';
      const { data } = await api.get(`${BASE}/files/deleted${params}`);
      setRecycleRows(data.rows || []);
      setRecycleGrace(data.graceDays ?? null);
    } catch { setRecycleRows([]); }
    finally { setRecycleLoading(false); }
  }, [clientId]);
  useEffect(() => { if (recycleOpen) loadRecycle(); }, [recycleOpen, loadRecycle]);
  async function restoreFile(row) {
    setRestoringId(row.id);
    try {
      await api.post(`${BASE}/files/${row.id}/restore`);
      await loadRecycle();
      refresh();
    } catch (err) { window.alert(errMsg(err, 'Could not restore the file.')); }
    finally { setRestoringId(null); }
  }

  // Move-folder modal (owner order 2026-09-02): pick a new parent within the
  // client, or — Admin/Power User — another client entirely (the subtree and
  // every file in it change owner).
  const [movingFolder, setMovingFolder] = useState(null); // node | null
  const [moveParentId, setMoveParentId] = useState('');
  const [moveClientId, setMoveClientId] = useState('');
  const [movingBusy,   setMovingBusy]   = useState(false);
  function openMoveFolder(node) {
    setMovingFolder(node);
    setMoveParentId('');
    setMoveClientId(clientId || '');
  }
  async function submitMoveFolder() {
    if (!movingFolder || movingBusy) return;
    const crossClient = isAdmin && moveClientId && moveClientId !== clientId;
    if (crossClient) {
      const target = clients.find(c => c.id === moveClientId);
      if (!window.confirm(`Move "${movingFolder.name}" (and everything inside it) to ${target?.name || 'that project'}?`)) return;
    }
    setMovingBusy(true);
    try {
      const body = { parentId: moveParentId || null };
      if (crossClient) body.clientId = moveClientId;
      await api.patch(`${BASE}/folders/${movingFolder.id}`, body);
      setMovingFolder(null);
      if (selected === movingFolder.id && crossClient) openFolder('all', { replace: true });
      refresh();
    } catch (err) { window.alert(errMsg(err, 'Could not move the folder.')); }
    finally { setMovingBusy(false); }
  }
  // Flatten the tree for the parent picker, excluding the moving folder's own
  // subtree (the server refuses those too — this just keeps them unclickable).
  function flattenTree(nodes, depth = 0, excludeId = null, out = []) {
    for (const n of nodes || []) {
      if (n.id === excludeId) continue;
      out.push({ id: n.id, name: n.name, depth });
      flattenTree(n.children, depth + 1, excludeId, out);
    }
    return out;
  }

  async function deleteFolder(node) {
    if (!window.confirm(`Delete the folder "${node.name}"?`)) return;
    try {
      await api.delete(`${BASE}/folders/${node.id}`);
      if (selected === node.id) openFolder('all', { replace: true });
      refresh();
    } catch (err) {
      // The 409 body carries the blocking counts — surface it plainly.
      window.alert(errMsg(err, 'Could not delete the folder.'));
    }
  }

  // Path shown above the dropzone and used to disable upload at "All files".
  function findNode(nodes, id) {
    for (const n of nodes) {
      if (n.id === id) return n;
      const hit = findNode(n.children || [], id);
      if (hit) return hit;
    }
    return null;
  }
  const selectedNode = (selected !== 'all' && selected !== 'unfiled') ? findNode(tree, selected) : null;
  const uploadTargetPath = selected === 'unfiled' ? 'Unfiled' : selectedNode?.path || null;

  // The two uploads have DIFFERENT enable rules on purpose. A file upload needs
  // a destination folder, so it is disabled at "All files". A folder upload
  // brings its own destination structure, so it can run there — it just creates
  // at the client root. Collapsing these into one control that silently behaves
  // differently depending on what was dragged would be unpredictable.
  const canUploadFiles  = canWrite && !!clientId && selected !== 'all';
  const canUploadFolder = canWrite && !!clientId;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // "All files" and "Unfiled" both stand at the client root.
  const atRoot = selected === 'all' || selected === 'unfiled';

  // Up one level. There is ALWAYS a level above wherever this renders, so the
  // control is never disabled: standing at the client root, the level above is
  // the rollup. The breadcrumb is the ancestor chain, so the parent is simply
  // the crumb before the last one — no tree walk, and it is right even when the
  // sidebar has not loaded.
  const goUp = useCallback(() => {
    const crumbs = browse?.folder?.breadcrumb || [];
    if (crumbs.length > 1)       openFolder(crumbs[crumbs.length - 2].id);  // parent folder
    else if (crumbs.length === 1) openFolder('all');                        // depth-0 → client root
    else                          toRollup();                               // client root → rollup
  }, [browse, openFolder, toRollup]);

  // The header's folder line counts the CARDS on screen — the direct child
  // folders — and totals what is inside them. The asset line beside it counts
  // only what this folder holds directly. Deliberately two measures.
  const cardCount = browse?.children?.length || 0;
  const cardBytes = (browse?.children || []).reduce((a, c) => a + bytesNum(c.totalBytes), 0);

  // The Folder column earns its place only when the rows can come from more than
  // one folder: "All files", or any search (which is unscoped across the client).
  const spansFolders = selected === 'all' || !!search.trim();
  const LIST_COLS = spansFolders
    ? '1fr 90px 120px 120px 100px 120px 110px'
    : '1fr 90px 130px 130px 100px 110px';

  // Bulk delete (owner order 2026-09-02): tick boxes on file rows (list view),
  // a warning popup, then the SAME per-file soft-delete endpoint in a loop —
  // per-row permission (Admin or own uploader) enforced server-side; refused
  // rows are skipped and reported, never forced.
  const canBulkDelete = !isExternal;
  const listCols = canBulkDelete ? `26px ${LIST_COLS}` : LIST_COLS;
  const deletableFiles = canBulkDelete ? files.filter(f => mayDeleteFile(deletePerm, f)) : [];

  function toggleFileSelect(id) {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    const ids = [...selectedFiles];
    if (!ids.length || bulkDeleting) return;
    setBulkDeleting(true);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        await api.delete(`${BASE}/files/${id}`);
        deleted++;
      } catch (err) {
        const f = files.find(x => x.id === id);
        skipped.push(`${f?.fileName || id} — ${errMsg(err, 'failed')}`);
      }
    }
    setBulkDeleting(false);
    setBulkDeleteOpen(false);
    setSelectedFiles(new Set());
    refresh();
    if (skipped.length) {
      alert(`${deleted} of ${ids.length} deleted.\n\nSkipped ${skipped.length}:\n${skipped.slice(0, 10).join('\n')}${skipped.length > 10 ? `\n…and ${skipped.length - 10} more` : ''}`);
    }
  }

  return (
    // The page shell owns the vertical scroll. `.app` is `height: 100vh;
    // overflow: hidden`, so a page that doesn't provide its own scroll container
    // is simply clipped at the viewport with no way to reach what's below —
    // which is what happened here once the upload panel made the page tall.
    // Every other page does this (see `.body` in globals.css, and RawFootage);
    // Storage was the one that didn't.
    <div style={{ flex: 1, minWidth: 0, height: '100vh', overflowY: 'auto' }}>
    <div style={{ padding: '24px 28px', maxWidth: 1500, margin: '0 auto' }}>
      {/* Header — deliberately not the violet Raw Footage treatment */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 14, marginBottom: 18 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-primary)', margin: 0 }}>
          Storage <span style={{ color: ACCENT, fontWeight: 600 }}>— Cold Archive (Glacier Instant)</span>
        </h1>
        <p style={{ fontSize: 12, color: 'var(--tx-muted)', margin: '5px 0 0' }}>
          Long-term archive storage. Files here are not part of Raw Footage — promote one to Live
          Footage when it needs to be transcribed, matched or edited.
        </p>
      </div>

      {/* Top bar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {!isExternal && (
          <select
            value={clientId} onChange={e => e.target.value ? pickClient(e.target.value) : toRollup()}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)', fontSize: 13, fontFamily: 'var(--font)', minWidth: 200 }}
          >
            <option value="">Select a project…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <input
          value={search} onChange={e => setSearch(e.target.value)} placeholder="Search file names…"
          style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 12px', color: 'var(--tx-primary)', fontSize: 13, fontFamily: 'var(--font)', minWidth: 240, flex: 1, maxWidth: 360 }}
        />
        {canWrite && (
          <>
            <button
              onClick={() => setShowUpload(showUpload === 'files' ? null : 'files')}
              disabled={!canUploadFiles}
              title={!clientId ? 'Select a project first'
                : selected === 'all' ? 'Pick a folder (or Unfiled) to upload files into' : ''}
              style={uploadBtn(canUploadFiles, showUpload === 'files')}
            >⬆ Upload Files</button>
            <button
              onClick={() => setShowUpload(showUpload === 'folder' ? null : 'folder')}
              disabled={!canUploadFolder}
              title={!clientId ? 'Select a project first'
                : selected === 'all' ? 'Creates the folder structure at the project root' : ''}
              style={uploadBtn(canUploadFolder, showUpload === 'folder')}
            >⬆ Upload Folder</button>
          </>
        )}
      </div>

      {error && (
        <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', border: '1px solid var(--s-incomplete-t)', borderRadius: 8, padding: '9px 13px', fontSize: 12, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {canMigrate && (
        <FrameioMigrationPanel
          clientId={clientId}
          // 'all' and 'unfiled' are not real folders — a migration into either
          // means the client root, which is what the API treats null as.
          selectedFolderId={selected === 'all' || selected === 'unfiled' ? null : selected}
          onMigrated={() => { loadTree(); loadFiles(); }}
        />
      )}

      {showUpload && (showUpload === 'files' ? canUploadFiles : canUploadFolder) && (
        <UploadPanel
          // Remount when the mode or destination changes so a half-built queue
          // can never inherit the wrong destination.
          key={`${showUpload}-${selected}-${clientId}`}
          mode={showUpload}
          clientId={clientId}
          // At "All files" a folder upload anchors at the client root.
          folderId={(selected === 'unfiled' || selected === 'all') ? null : selected}
          folderPath={selected === 'all' ? 'Project root' : uploadTargetPath}
          isExternal={isExternal}
          onDone={refresh}
          onClose={() => setShowUpload(null)}
        />
      )}

      {!clientId ? (
        // The landing state is now the rollup, not a prompt to pick a client.
        // A caller scoped to exactly one client never gets here — the summary
        // effect above lands them straight in their own tree.
        summary
          ? <ClientRollup summary={summary} onPick={pickClient} />
          : <EmptyState title="Loading storage…" body="Reading what is archived across your projects." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 260px) 1fr', gap: 16, alignItems: 'start' }}>
          {/* Left: folder tree */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 10 }}>
            <div
              onClick={() => openFolder('all')}
              style={pseudoNode(selected === 'all')}
            >
              <span>▤</span><span style={{ flex: 1 }}>All files</span>
              <span style={{ fontSize: 10, color: 'var(--tx-faint)' }}>{counts.totalCount || ''}</span>
            </div>
            <div
              onClick={() => openFolder('unfiled')}
              style={pseudoNode(selected === 'unfiled')}
            >
              <span>◇</span><span style={{ flex: 1 }}>Unfiled</span>
              <span style={{ fontSize: 10, color: 'var(--tx-faint)' }}>{counts.unfiledCount || ''}</span>
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />

            {tree.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--tx-faint)', padding: '10px 8px', lineHeight: 1.5 }}>
                No folders yet.{canWrite ? ' Create one to start organising this archive.' : ''}
              </div>
            ) : tree.map(node => (
              <FolderNode
                key={node.id} node={node} depth={0}
                selectedId={selected} expanded={expanded} onToggle={toggle} onSelect={openFolder}
                onRename={renameFolder} onMove={canWrite ? openMoveFolder : null} onDelete={deleteFolder} canManage={canWrite}
              />
            ))}

            {canWrite && (
              <button
                onClick={newFolder}
                style={{ width: '100%', marginTop: 10, background: 'none', border: '1px dashed var(--border-mid)', color: 'var(--tx-muted)', cursor: 'pointer', fontSize: 12, padding: '7px 10px', borderRadius: 8, fontFamily: 'var(--font)' }}
              >+ New Folder</button>
            )}
          </div>

          {/* Right: breadcrumb, folder cards, then the assets themselves */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {/* Breadcrumb + the two count lines + the view toggle.
                The two numbers are DIFFERENT MEASURES and are worded so they
                cannot be read as the same one: a folder card says what is inside
                it all the way down, this line says what this folder holds. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12 }}>
                  {/* Up one level. Never disabled — at the client root the level
                      above is the rollup. */}
                  <button
                    onClick={goUp}
                    title={atRoot ? 'Back to all projects' : 'Up one level'}
                    style={{
                      background: 'transparent', border: `1px solid ${ACCENT}`, color: ACCENT,
                      cursor: 'pointer', fontSize: 12, fontWeight: 700, lineHeight: 1,
                      padding: '3px 8px', borderRadius: 6, fontFamily: 'var(--font)', flexShrink: 0,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = ACCENT_SOFT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >↑</button>
                  <span
                    onClick={() => openFolder('all')}
                    style={{ cursor: 'pointer', color: atRoot ? 'var(--tx-primary)' : ACCENT, fontWeight: 600 }}
                  >{browse?.folder?.clientName || 'Project'}</span>
                  {/* Crumbs come from the parent chain's NAMES. `path` is a slug
                      string and diverges from the display name on live —
                      "Bloopers (RAW)" paths as "Bloopers_RAW". */}
                  {(browse?.folder?.breadcrumb || []).map((c, i, arr) => (
                    <span key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'var(--tx-faint)' }}>›</span>
                      <span
                        onClick={() => openFolder(c.id)}
                        style={{ cursor: 'pointer', fontWeight: i === arr.length - 1 ? 700 : 600, color: i === arr.length - 1 ? 'var(--tx-primary)' : ACCENT }}
                      >{c.name}</span>
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
                  {cardCount > 0 && <>{cardCount} folder{cardCount === 1 ? '' : 's'} · {formatBytes(cardBytes)} inside · </>}
                  {selected === 'all'
                    ? <>{total.toLocaleString()} assets across this project</>
                    : <>{(browse?.assets?.count ?? 0).toLocaleString()} assets held here · {formatBytes(browse?.assets?.totalBytes)}</>}
                </div>
              </div>

              {/* Recycle bin — deleted files inside the grace window, with
                  restore-in-place (owner order 2026-09-02). */}
              {!isExternal && (
                <button
                  onClick={() => setRecycleOpen(true)}
                  title="Deleted files being held in the recovery window — restore them to where they were"
                  style={{
                    background: 'transparent', border: '1px solid var(--border-mid)', color: 'var(--tx-secondary)',
                    cursor: 'pointer', fontSize: 12, padding: '5px 11px', borderRadius: 7,
                    fontFamily: 'var(--font)', fontWeight: 600, flexShrink: 0, marginRight: 8,
                  }}
                >🗑 Recycle Bin</button>
              )}

              {/* List / grid. Changes only how assets render. */}
              <div style={{ display: 'flex', border: '1px solid var(--border-mid)', borderRadius: 7, overflow: 'hidden', flexShrink: 0 }}>
                {['list', 'grid'].map(m => (
                  <button
                    key={m}
                    onClick={() => setViewMode(m)}
                    title={m === 'list' ? 'List view' : 'Grid view'}
                    style={{
                      background: viewMode === m ? ACCENT_SOFT : 'transparent',
                      color: viewMode === m ? ACCENT : 'var(--tx-faint)',
                      border: 'none', cursor: 'pointer', fontSize: 12, padding: '5px 11px',
                      fontFamily: 'var(--font)', fontWeight: 600,
                    }}
                  >{m === 'list' ? '☰' : '▦'}</button>
                ))}
              </div>
            </div>

            {/* Folder cards — an ADDITIONAL way into the same selection state the
                sidebar tree drives. Clicking one is identical to clicking that
                node in the tree, and the tree highlight follows. */}
            {(browse?.children?.length > 0) && (
              <FolderCards
                folders={browse.children}
                onOpen={id => { openFolder(id); setExpanded(prev => new Set(prev).add(id)); }}
              />
            )}

            {viewMode === 'list' && canBulkDelete && selectedFiles.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid var(--border)', background: 'rgba(212,85,85,0.05)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)' }}>
                  {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={bulkDeleting}
                  style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
                >
                  Delete {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''}…
                </button>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelectedFiles(new Set())} disabled={bulkDeleting}>
                  Clear
                </button>
              </div>
            )}

            {viewMode === 'list' && (
              <div style={{ display: 'grid', gridTemplateColumns: listCols, gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--tx-faint)', fontWeight: 700 }}>
                {canBulkDelete && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      title="Select all files you may delete"
                      checked={deletableFiles.length > 0 && deletableFiles.every(f => selectedFiles.has(f.id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedFiles(new Set(deletableFiles.map(f => f.id)));
                        else setSelectedFiles(new Set());
                      }}
                      style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                    />
                  </div>
                )}
                <div>File name</div><div>Size</div><div>Source</div><div>Uploaded by</div><div>Date</div>
                {spansFolders && <div>Folder</div>}
                <div>Status</div>
              </div>
            )}

            {loading ? (
              <div style={{ padding: 28, textAlign: 'center', fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
            ) : files.length === 0 ? (
              // With cards above, "empty" must not claim the folder is empty when
              // it holds sub-folders — it holds no FILES of its own.
              <EmptyState
                bare
                title={search ? 'No matching files'
                  : browse?.children?.length ? 'No files directly in this folder'
                  : selected === 'unfiled' ? 'Nothing unfiled' : 'This folder is empty'}
                body={search ? 'Try a different search term.'
                  : browse?.children?.length ? 'Open a folder above to see what is inside it.'
                  : canWrite ? 'Upload files here to archive them.' : 'Nothing has been archived here yet.'}
              />
            ) : viewMode === 'grid' ? (
              <FileGrid files={files} onSelect={setDetail} />
            ) : files.map(f => (
              <div
                key={f.id}
                onClick={() => setDetail(f)}
                style={{ display: 'grid', gridTemplateColumns: listCols, gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--tx-secondary)', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {canBulkDelete && (
                  <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                    {mayDeleteFile(deletePerm, f) ? (
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(f.id)}
                        onChange={() => toggleFileSelect(f.id)}
                        style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                      />
                    ) : <span />}
                  </div>
                )}
                <div style={{ color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}</div>
                <div>{formatBytes(f.fileSizeBytes)}</div>
                <div style={{ color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {SOURCE_LABELS[f.source] || f.source}
                  <FootageArchiveChip file={f} compact />
                </div>
                <div style={{ color: 'var(--tx-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.uploadedByName || '—'}</div>
                <div style={{ color: 'var(--tx-muted)' }}>{fmtDate(f.createdAt)}</div>
                {spansFolders && (
                  <div style={{ color: 'var(--tx-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.folderName || 'Unfiled'}</div>
                )}
                <div><PromoteChip file={f} compact /></div>
              </div>
            ))}

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '10px 14px', fontSize: 12, color: 'var(--tx-muted)' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={smallBtn('var(--border-mid)', 'var(--tx-secondary)')}>Prev</button>
                <span>Page {page} of {totalPages} · {total} files</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={smallBtn('var(--border-mid)', 'var(--tx-secondary)')}>Next</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk-delete warning popup — same retention-aware wording as the
          single-file dialog. Files soft-delete into the Deleted view; the
          reaper (when armed) destroys the cold-storage objects after the
          grace window. */}
      {bulkDeleteOpen && (
        <div className="modal-overlay" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: 'var(--s-incomplete-t)' }}>
                ⚠ Delete {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} from Storage?
              </div>
              <button className="vc-close" onClick={() => !bulkDeleting && setBulkDeleteOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: 'var(--tx-primary)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {retention?.reaperArmed === true && retention?.graceDays
                  ? `They disappear from Storage straight away.\n\nThe files themselves are then permanently deleted from cold storage after ${retention.graceDays} days — no sooner. Only an Admin can restore them, and only inside that window.\n\nOnce the ${retention.graceDays} days are up this cannot be undone.`
                  : `They disappear from Storage straight away. The files themselves are kept in cold storage and can be restored by an Admin from the Deleted view.`}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.4)' }}
              >
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <StorageDownloadDock />

      {/* Recycle bin modal */}
      {recycleOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 95, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={() => setRecycleOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               style={{ width: 760, maxWidth: '94vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)' }}>🗑 Recycle Bin</div>
                <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 3, lineHeight: 1.5 }}>
                  Deleted files{clientId ? ' for this project' : ''}, held in the {recycleGrace || 30}-day recovery window.
                  Restore puts a file straight back where it was.
                </div>
              </div>
              <button onClick={() => setRecycleOpen(false)} style={{ ...iconBtn, fontSize: 18 }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {recycleLoading ? (
                <div style={{ padding: 28, textAlign: 'center', fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
              ) : recycleRows.length === 0 ? (
                <div style={{ padding: 28, textAlign: 'center', fontSize: 12, color: 'var(--tx-faint)' }}>The bin is empty.</div>
              ) : recycleRows.map(r => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 150px 90px', gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 12, alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--tx-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.fileName}</div>
                    <div style={{ color: 'var(--tx-muted)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(r.clientName || 'Unassigned')} · {r.folderPath || 'Unfiled'} · {formatBytes(r.fileSizeBytes)}
                    </div>
                  </div>
                  <div style={{ color: 'var(--tx-muted)', fontSize: 11 }}>
                    {r.deletedAt ? fmtDate(r.deletedAt) : '—'}
                    {r.deletedByName ? <div style={{ color: 'var(--tx-faint)' }}>{r.deletedByName}</div> : null}
                  </div>
                  <div style={{ fontSize: 11, color: r.objectRemoved ? 'var(--s-incomplete-t)' : 'var(--tx-muted)' }}>
                    {r.objectRemoved
                      ? 'Object removed — gone'
                      : r.restorableUntil ? `Held until ${fmtDate(r.restorableUntil)}` : 'Held (no expiry set)'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {r.restorable && (
                      <button
                        onClick={() => restoreFile(r)}
                        disabled={restoringId === r.id}
                        style={{ background: 'transparent', border: `1px solid ${ACCENT}`, color: ACCENT, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, fontFamily: 'var(--font)' }}
                      >
                        {restoringId === r.id ? 'Restoring…' : 'Restore'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Move-folder modal */}
      {movingFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 95, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
             onClick={() => !movingBusy && setMovingFolder(null)}>
          <div onClick={e => e.stopPropagation()}
               style={{ width: 420, maxWidth: '92vw', background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--tx-primary)', marginBottom: 12 }}>
              Move “{movingFolder.name}”
            </div>
            {isAdmin && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'block', marginBottom: 4 }}>Project</label>
                <select
                  value={moveClientId}
                  onChange={e => { setMoveClientId(e.target.value); setMoveParentId(''); }}
                  style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)' }}
                >
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'block', marginBottom: 4 }}>
                New parent folder{isAdmin && moveClientId !== clientId ? ' (root of the target project)' : ''}
              </label>
              <select
                value={moveParentId}
                onChange={e => setMoveParentId(e.target.value)}
                disabled={isAdmin && moveClientId !== clientId}
                title={isAdmin && moveClientId !== clientId ? 'A cross-project move lands at the target project’s root' : undefined}
                style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 8, padding: '8px 10px', color: 'var(--tx-primary)', fontSize: 12, fontFamily: 'var(--font)' }}
              >
                <option value="">Root (top level)</option>
                {flattenTree(tree, 0, movingFolder.id).map(f => (
                  <option key={f.id} value={f.id}>{' '.repeat(f.depth * 3)}{f.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setMovingFolder(null)} disabled={movingBusy}>Cancel</button>
              <button className="btn-primary" onClick={submitMoveFolder} disabled={movingBusy} style={{ fontSize: 12 }}>
                {movingBusy ? 'Moving…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <DetailDrawer
          file={detail}
          folders={tree}
          canPromote={canPromote}
          canWrite={canWrite}
          deletePerm={deletePerm}
          retention={retention}
          onClose={() => { setDetail(null); dismissFinishedDownloads(); }}
          onChanged={refresh}
        />
      )}
    </div>
    </div>
  );
}

function uploadBtn(enabled, active) {
  return {
    background: active ? ACCENT_SOFT : 'transparent',
    border: `1px solid ${enabled ? ACCENT : 'var(--border)'}`,
    color: enabled ? ACCENT : 'var(--tx-faint)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 8, fontFamily: 'var(--font)',
    opacity: enabled ? 1 : 0.6,
  };
}

function pseudoNode(active) {
  return {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
    background: active ? ACCENT_SOFT : 'transparent',
    color: active ? ACCENT : 'var(--tx-secondary)',
  };
}

function EmptyState({ title, body, bare }) {
  return (
    <div style={{
      padding: '36px 24px', textAlign: 'center',
      background: bare ? 'transparent' : 'var(--bg-card)',
      border: bare ? 'none' : '1px solid var(--border)',
      borderRadius: bare ? 0 : 12,
    }}>
      <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }}>▤</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-secondary)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--tx-faint)', marginTop: 4 }}>{body}</div>
    </div>
  );
}
